<template>
  <v-card :outlined="outlined"
          :class="`mb-3 rounded-card ${outlined ? '': 'card-shadow'}`">
    <v-card-text class="text--primary">
      <v-list-item dense>
        <v-row dense>
          <v-col cols="12" sm="5">
            <px-produto-descricao
              :especificacao="compraProduto.especificacaoProdutoOrigem
              || compraProduto.especificacaoProduto"
              :produto="compraProduto.produto"/>&nbsp;
            <div class="caption">
              <px-gestao-entrega-produto-situacao
                show-suffix-icon
                :situacao="compraProduto.situacaoEntrega"/>
            </div>
            <div class="caption">
              <px-cod-interno-cod-proxpect-compra :compra="compraProduto.compra"
                                                  prefix="# "/>
            </div>
          </v-col>
          <v-col cols="12" sm="2">
            {{
              compraProduto.saldoEntregue
                | number(getCurrencyFormat(compraProduto.produto.casasDecimais))
            }}/{{
              compraProduto.quantidade
                | number(getCurrencyFormat(compraProduto.produto.casasDecimais))
            }}&nbsp;
            {{ compraProduto.unidadeMedida.descricao }}
            <div class="caption">
              Saldo
            </div>
          </v-col>
          <v-col cols="12" sm="2" v-if="compraProduto.compra">
            {{ fornecedor | truncate(30) }}
          </v-col>
          <v-col cols="12" sm="3">
          <span>
          {{ dataPrevisaoEntregaGeral | date }}
            <span v-if="isAtrasado" class="error--text">
              <v-icon right class="pb-1 error--text">mdi-circle-medium</v-icon>
              Atrasado
            </span>
            <span v-if="isHoje" class="text--primary--color">
              <v-icon right class="pb-1" color="primary">
                mdi-circle-medium
              </v-icon>
              Hoje
            </span>
          </span>
            <div class="caption">
              {{ dataPrevisaoEntregaGeralLabel }}
            </div>
          </v-col>
        </v-row>
        <v-list-item-action>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon :disabled="loading || loadingOperation"
                     v-if="showCheckFaturamento"
                     v-on="on"
                     @click="onClickFaturarItem">
                <v-icon>
                  mdi-file-check-outline
                </v-icon>
              </v-btn>
            </template>
            <span>Marcar como faturado</span>
          </v-tooltip>
        </v-list-item-action>
        <v-list-item-action>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon :disabled="loading || loadingOperation"
                     v-if="!isEntregue"
                     v-on="on"
                     @click="onClickEntregarItem">
                <v-icon>
                  mdi-check
                </v-icon>
              </v-btn>
            </template>
            <span>Marcar como entregue</span>
          </v-tooltip>
        </v-list-item-action>
        <v-list-item-action>
          <template v-if="!isEntregue">
            <px-gestao-entrega-produto-dividir-saldo-dialog :saldos="sortedSaldos"
                                                            :compra-produto="compraProduto"
                                                            @change="emitChange(compraProduto)"/>
          </template>
        </v-list-item-action>
        <v-list-item-action v-if="compraProduto.compra">
          <px-compra-detalhes-dialog-view :compra-id="compraProduto.compra.id"
                                          :show-entregas="false"/>
        </v-list-item-action>
        <v-list-item-action>
          <v-btn icon @click="onClickExpand">
            <v-icon>
              {{ expandIcon }}
            </v-icon>
          </v-btn>
        </v-list-item-action>
      </v-list-item>
      <transition name="scroll-y-transition">
        <div class="mt-3" v-if="expanded">
          <px-gestao-entrega-produtos-compras-item-content
            @change="onChangeSaldo"
            :saldos="sortedSaldos"
            :controla-faturamento="controlaFaturamento"
            :compra-produto="compraProduto"/>
        </div>
      </transition>
    </v-card-text>
  </v-card>
</template>

<script>
import { CasaDecimalUtils } from 'px-components';
import GestaoEntregaProdutosComprasItemContent
  from '@/modules/gestao-entregas/GestaoEntregaProdutosComprasItemContent.vue';
import GestaoEntregaProdutoDividirSaldoDialog
  from '@/modules/gestao-entregas/GestaoEntregaProdutoDividirSaldoDialog.vue';
import GestaoEntregaService from '@/modules/gestao-entregas/gestao-entrega-service';
import { SituacaoEntregaCompra, SituacaoEntregaSaldo } from 'px-business-components';
import { sortBy } from 'lodash';
import { formatToCode } from '@/utils/moment-date-utils';
import moment from 'moment';

const situacoesEntregaFinalizadas = [SituacaoEntregaSaldo.keys.ENTREGUE.key,
  SituacaoEntregaSaldo.keys.CANCELADO.key];

const getProximoSaldoPendente = (saldos) => {
  if (!saldos || saldos.length === 0) return null;
  const saldosPendentes = saldos
    .filter(saldo => !situacoesEntregaFinalizadas.includes(saldo.situacaoEntrega))
    .sort((a, b) => new Date(a.dataPrevisaoEntrega) - new Date(b.dataPrevisaoEntrega));
  return saldosPendentes.length > 0 ? saldosPendentes[0] : null;
};

const getUltimoSaldoEntregue = (saldos) => {
  if (!saldos || saldos.length === 0) return null;
  const saldosEntregues = saldos
    .filter(saldo => saldo.situacaoEntrega === SituacaoEntregaSaldo.keys.ENTREGUE.key)
    .sort((a, b) => new Date(b.dataRegistro) - new Date(a.dataRegistro));
  return saldosEntregues.length > 0 ? saldosEntregues[0] : null;
};

export default {
  props: {
    compraProduto: Object,
    loading: {
      type: Boolean,
      default: false,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    controlaFaturamento: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expanded: false,
      loadingOperation: false,
      headers: [
        {
          text: 'Produto',
          sortable: false,
          value: 'descricao',
        },
        {
          text: 'Quantidade',
          value: 'quantidade',
          sortable: false,
        },
        {
          text: '',
          value: 'options',
          align: 'end',
        },
        {
          text: '',
          value: 'data-table-expand',
        },
      ],
    };
  },
  computed: {
    getCurrencyFormat() {
      return item => CasaDecimalUtils.getCurrencyFormat(item);
    },
    expandIcon() {
      return this.expanded ? 'mdi-chevron-up' : 'mdi-chevron-down';
    },
    isEntregue() {
      return this.compraProduto.situacaoEntrega === SituacaoEntregaCompra.keys.ENTREGUE.key;
    },
    dataPrevisaoEntregaGeralLabel() {
      return this.isEntregue ? 'Última entrega' : 'Previsão de entrega';
    },
    dataPrevisaoEntregaGeral() {
      if (this.isEntregue) {
        const ultimoSaldoEntregue = getUltimoSaldoEntregue(this.compraProduto.saldosEntregas);
        return ultimoSaldoEntregue ? ultimoSaldoEntregue.dataEntrega
          : this.compraProduto.dataPrevisaoEntrega;
      }
      const proximoSaldoPendente = getProximoSaldoPendente(this.compraProduto.saldosEntregas);
      return proximoSaldoPendente ? proximoSaldoPendente.dataPrevisaoEntrega
        : this.compraProduto.dataPrevisaoEntrega;
    },
    sortedSaldos() {
      return sortBy(this.compraProduto.saldosEntregas, ['dataPrevisaoEntrega', 'id']);
    },
    isAtrasado() {
      if (this.isEntregue) {
        return false;
      }
      const dataPrevisaoEntrega = moment(this.dataPrevisaoEntregaGeral);
      const dataAtual = moment();
      return dataPrevisaoEntrega.isBefore(dataAtual, 'day');
    },
    isHoje() {
      if (this.isEntregue) {
        return false;
      }
      const dataPrevisaoEntrega = moment(this.dataPrevisaoEntregaGeral);
      const dataAtual = moment();
      return dataPrevisaoEntrega.isSame(dataAtual, 'day');
    },
    showCheckFaturamento() {
      const hasSaldoNaoFaturado = this.compraProduto.saldosEntregas
        .find(s => s.situacaoEntrega === SituacaoEntregaCompra.keys.PENDENTE.key);
      return this.controlaFaturamento && !!hasSaldoNaoFaturado;
    },
    fornecedor() {
      return this.compraProduto.compra.nomeInternoFornecedor
        || this.compraProduto.compra.fornecedor.pessoa.nome;
    },
  },
  methods: {
    onClickExpand() {
      this.expanded = !this.expanded;
    },
    onClickFaturarItem() {
      this.loadingOperation = true;
      GestaoEntregaService.faturarItemCompra(this.compraProduto.id, {
        dataRegistro: formatToCode(moment()),
      })
        .then(({ data }) => {
          this.emitChange(data);
        })
        .finally(() => {
          this.loadingOperation = false;
        });
    },
    onClickEntregarItem() {
      this.loadingOperation = true;
      GestaoEntregaService.entregarItemCompra(this.compraProduto.id, {
        dataRegistro: formatToCode(moment()),
      })
        .then(({ data }) => {
          this.emitChange(data);
        })
        .finally(() => {
          this.loadingOperation = false;
        });
    },
    emitChange(data) {
      this.$emit('change', data);
    },
    onChangeSaldo(data) {
      this.emitChange(data);
    },
  },
  components: {
    pxCompraDetalhesDialogView: () => import('@/modules/compra/detalhes/CompraDetalhesDialogView.vue'),
    pxGestaoEntregaProdutosComprasItemContent: GestaoEntregaProdutosComprasItemContent,
    pxGestaoEntregaProdutoDividirSaldoDialog: GestaoEntregaProdutoDividirSaldoDialog,
  },
};
</script>
